import React, { useMemo } from 'react';

import { useDrawer } from '@common/drawers/dynamicProvider.drawer';
import EnhancedButton from '@components/button/EnhancedButton';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import DateService from '@core/services/DateService';
import FormattingService from '@core/services/FormattingService';

import { FormValues } from '../types';
import { dateToFormValue } from '../utils/amendmentIssueDate.utils';

import { useFormikContext } from 'formik';

type Props = {
  status: 'NEW' | 'IN_PROGRESS' | 'CONFIRMED' | 'CANCELLED' | string;
  amendmentSubTypeLovs: Record<string, string>;
  amendmentSubTypeDisabled?: boolean;
  systemReferenceDate: string;
  policyEffectiveDate: string;
  policyExpiryDate: string;
  amendmentId: string;
  amendmentIssueDateMinDate: Date;
  amendmentIssueDateMaxDate: Date;
  amendmentIssueDateDisabled: boolean;
};

const addMonths = (date: Date, months: number): Date => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);

  return d;
};

export const AmendmentEndorsementDetailsAtom: React.FC<Props> = ({
  status,
  amendmentSubTypeLovs,
  amendmentSubTypeDisabled = false,
  systemReferenceDate,
  policyEffectiveDate,
  policyExpiryDate,
  amendmentId,
  amendmentIssueDateMinDate,
  amendmentIssueDateMaxDate,
  amendmentIssueDateDisabled
}) => {
  console.log(policyExpiryDate);
  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<FormValues>();
  const { openDrawer } = useDrawer();

  const s = String(status ?? '').toUpperCase();
  const isLocked = s === 'CONFIRMED' || s === 'CANCELLED';

  const v = values?.amendmentDetails ?? {
    amendmentType: '',
    amendmentSubType: '',
    amendmentIssueDate: '',
    amendmentEffectiveDate: '',
    amendmentReason: ''
  };

  const showSubType = useMemo(() => {
    const t = v.amendmentType;

    return t === 'PolicyEndorsement';
  }, [v.amendmentType]);

  const showReason = useMemo(() => {
    const t = v.amendmentType;

    return t === 'PolicyCancellation';
  }, [v.amendmentType]);

  const amendmentTypeDisplay = FormattingService.formatCamelCaseToDisplay(
    v.amendmentType
  );

  // Determine if modify button should be shown
  const showModifyButton =
    !isLocked && v.amendmentType === 'PolicyCancellation';

  const handleModifyClick = () => {
    const amendmentData = {
      amendmentID: amendmentId,
      amendmentReason: v.amendmentReason || '',
      amendmentEffectiveDate: v.amendmentEffectiveDate || '',
      amendmentIssueDate: v.amendmentIssueDate || systemReferenceDate,
      originalEffectiveDate: v.amendmentEffectiveDate || ''
    };

    openDrawer('modifyAmendmentDetailsDrawer', 'Modify Amendment Details', {
      amendmentData,
      policyEffectiveDate,
      policyExpiryDate
    });
  };

  const { minDate, maxDate } = useMemo(() => {
    const refDate = new Date(systemReferenceDate);
    const policyEffDate = policyEffectiveDate
      ? new Date(policyEffectiveDate)
      : null;
    const policyExpDate = policyExpiryDate ? new Date(policyExpiryDate) : null;

    const baseMin = addMonths(refDate, -3);
    const baseMax = addMonths(refDate, 1);

    let calculatedMinDate = baseMin;
    if (policyEffDate && policyEffDate > calculatedMinDate) {
      calculatedMinDate = policyEffDate;
    }

    let calculatedMaxDate = baseMax;
    if (policyExpDate && policyExpDate < calculatedMaxDate) {
      calculatedMaxDate = policyExpDate;
    }

    return {
      minDate: calculatedMinDate,
      maxDate: calculatedMaxDate
    };
  }, [systemReferenceDate, policyEffectiveDate, policyExpiryDate]);

  return (
    <WidgetPaper>
      <WidgetSection
        actions={
          showModifyButton ? (
            <div>
              <EnhancedButton isPrimary onClick={handleModifyClick}>
                Modify
              </EnhancedButton>
            </div>
          ) : undefined
        }
        title="Amendment Details"
        isFieldsGrid
        fieldsPerRow={2}
      >
        <TextInputFormField
          key="amendmentType"
          name="amendmentDetails.amendmentType"
          title="Amendment Type*"
          placeholder="Amendment Type"
          value={amendmentTypeDisplay}
          error={
            touched?.amendmentDetails?.amendmentType
              ? (errors as any)?.amendmentDetails?.amendmentType
              : ''
          }
          onBlur={(e) => handleBlur(e)}
          disabled
          material
          onChange={() => {}}
        />

        {showReason && (
          <TextInputFormField
            key="amendmentReason"
            name="amendmentDetails.amendmentReason"
            title="Cancellation Reason*"
            placeholder="Cancellation Reason"
            value={v.amendmentReason}
            error={
              touched?.amendmentDetails?.amendmentReason
                ? (errors as any)?.amendmentDetails?.amendmentReason
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            disabled
            material
            onChange={() => {}}
          />
        )}

        {showSubType && (
          <SelectFormField
            key={`amendmentSubType-${v.amendmentSubType}`}
            name="amendmentDetails.amendmentSubType"
            title="Amendment Sub-type*"
            placeholder="Amendment Sub-type"
            value={v.amendmentSubType}
            error={
              touched?.amendmentDetails?.amendmentSubType
                ? (errors as any)?.amendmentDetails?.amendmentSubType
                : ''
            }
            onBlur={(_, e) => handleBlur(e)}
            onChange={(val) =>
              setFieldValue('amendmentDetails.amendmentSubType', val, false)
            }
            disabled={amendmentSubTypeDisabled}
            selectOptions={amendmentSubTypeLovs}
            material
          />
        )}
      </WidgetSection>

      <WidgetSection isFieldsGrid fieldsPerRow={2}>
        <DatePickerFormField
          name="amendmentDetails.amendmentIssueDate"
          title="Amendment Issue Date*"
          placeholder="Amendment Issue Date"
          value={v.amendmentIssueDate}
          error={
            touched?.amendmentDetails?.amendmentIssueDate
              ? (errors as any)?.amendmentDetails?.amendmentIssueDate
              : ''
          }
          onBlur={(e) => handleBlur(e)}
          onDateChange={(val) => {
            if (!val) {
              return;
            }
            const dateString = val instanceof Date ? dateToFormValue(val) : val;
            setFieldValue(
              'amendmentDetails.amendmentIssueDate',
              dateString,
              false
            );
          }}
          disabled={amendmentIssueDateDisabled}
          minDate={amendmentIssueDateMinDate}
          maxDate={amendmentIssueDateMaxDate}
          material
        />

        <DatePickerFormField
          name="amendmentDetails.amendmentEffectiveDate"
          title="Amendment Effective Date*"
          placeholder="Amendment Effective Date"
          value={v.amendmentEffectiveDate}
          error={
            touched?.amendmentDetails?.amendmentEffectiveDate
              ? (errors as any)?.amendmentDetails?.amendmentEffectiveDate
              : ''
          }
          onBlur={(e) => handleBlur(e)}
          onDateChange={(val) => {
            const formattedDate = DateService.formatDateTimeBackend(val);
            setFieldValue(
              'amendmentDetails.amendmentEffectiveDate',
              formattedDate,
              false
            );
          }}
          disabled={isLocked}
          minDate={minDate}
          maxDate={maxDate}
          material
        />
      </WidgetSection>
    </WidgetPaper>
  );
};
